/**
 * api/session.ts —— 会话能力
 *
 * 会话是「我」与某联系人之间的对话容器。对用户而言它没有独立名字，
 * 永远通过「和谁」来指代 —— 所以这个 API 的参数大量使用联系人姓名。
 *
 * `list()` 返回的是**会话视图**（含联系人姓名、最后一条消息、未读数），
 * 因为菜单里光有会话 id 对模型毫无意义，它需要能转述给用户的信息。
 */

import { sessionsRepo } from '@/data/repo.sessions';
import { messagesRepo } from '@/data/repo.messages';
import { contactsRepo } from '@/data/repo.contacts';
import { pushOverlay } from '@/core/ui-store';
import { eventBus } from '@/core/event-bus';
import { resolveContact } from './contact';
import { RuntimeError, type ObjectDef } from '../registry';
import type { Contact } from '@/types/models';

/** 把一行会话整理成模型能读懂、也能转述给用户的对象 */
async function toView(s: {
  id: string;
  contactId: string;
  lastMessageAt: number;
  lastPreview: string;
  unread: number;
  pinned: boolean;
}): Promise<Record<string, unknown>> {
  const c = await contactsRepo.get(s.contactId);
  return {
    id: s.id,
    who: c?.name ?? '（已删除的联系人）',
    lastMessage: s.lastPreview,
    lastMessageAt: s.lastMessageAt,
    unread: s.unread,
    pinned: s.pinned,
  };
}

export const sessionApi = {
  name: 'session',
  desc: '与某个联系人的对话（会话）',
  userDesc: '聊天 —— 发消息、读聊天记录、标记已读、置顶',
  methods: [
    {
      name: 'list',
      kind: 'query',
      desc: '列出全部会话，按最近消息时间倒序。用户问「有哪些会话」「最近谁找过我」时用这个。',
      params: [],
      successText: '已读取会话列表',
      run: async () => {
        const list = await sessionsRepo.listWithContact();
        const views = await Promise.all(
          list.map(async (s) => ({ ...(await toView(s)), who: s.contact?.name ?? '（未知）' })),
        );
        return { value: views };
      },
    },
    {
      name: 'send',
      desc: '给某个联系人发消息（若还没有会话就自动建立）。用户说「给 xxx 发消息说 yyy」时用这个。',
      params: [
        { name: 'name', type: 'string' as const, desc: '联系人姓名', required: true },
        { name: 'text', type: 'string' as const, desc: '消息内容', required: true },
      ],
      successText: (args: unknown[]) => `已给「${String(args[0])}」发送：${String(args[1])}`,
      run: async (args: unknown[]) => {
        const [name, text] = args as [string, string];
        const c = await resolveContact(name);
        const session = await sessionsRepo.ensureForContact(c.id);
        await messagesRepo.append({ sessionId: session.id, sender: 'me', text });
        return { value: { sessionId: session.id, to: c.name } };
      },
    },
    {
      name: 'messages',
      kind: 'query',
      desc: '读某个联系人的最近聊天记录。用户问「xxx 跟我说了什么」「最近的聊天记录」时用这个。',
      params: [
        { name: 'name', type: 'string' as const, desc: '联系人姓名', required: true },
        { name: 'limit', type: 'number' as const, desc: '读多少条，默认 20', required: false, min: 1, max: 100 },
      ],
      successText: (args: unknown[]) => `已读取与「${String(args[0])}」的聊天记录`,
      run: async (args: unknown[]) => {
        const [name, limit] = args as [string, number | undefined];
        const c = await resolveContact(name);
        const session = await sessionsRepo.ensureForContact(c.id);
        const list = await messagesRepo.latest(session.id, limit ?? 20);
        const views = list.map((m) => ({
          from: m.sender === 'me' ? '我' : c.name,
          text: m.text,
          type: m.type,
          at: m.createdAt,
        }));
        return { value: views };
      },
    },
    {
      name: 'markRead',
      desc: '把与某联系人的会话标记为已读。',
      params: [{ name: 'name', type: 'string' as const, desc: '联系人姓名', required: true }],
      successText: (args: unknown[]) => `已把与「${String(args[0])}」的会话标为已读`,
      run: async (args: unknown[]) => {
        const c = await resolveContact(String(args[0]));
        const session = await sessionsRepo.ensureForContact(c.id);
        await sessionsRepo.markRead(session.id);
        return {};
      },
    },
    {
      name: 'markAllRead',
      desc: '一键把所有会话都标为已读。用户说「全部已读」「清掉所有小红点」时用这个。',
      params: [],
      successText: '已把全部会话标为已读',
      run: async () => {
        const n = await sessionsRepo.markAllRead();
        return { value: { count: n } };
      },
    },
    {
      name: 'pin',
      desc: '置顶或取消置顶某个会话。',
      params: [
        { name: 'name', type: 'string' as const, desc: '联系人姓名', required: true },
        { name: 'pinned', type: 'boolean' as const, desc: 'true 置顶，false 取消', required: true },
      ],
      successText: (args: unknown[]) =>
        args[1] ? `已置顶与「${String(args[0])}」的会话` : `已取消置顶与「${String(args[0])}」的会话`,
      run: async (args: unknown[]) => {
        const [name, pinned] = args as [string, boolean];
        const c = await resolveContact(name);
        const session = await sessionsRepo.ensureForContact(c.id);
        await sessionsRepo.setPinned(session.id, pinned);
        return {};
      },
    },
    {
      name: 'open',
      desc: '打开与某联系人的聊天界面。',
      params: [{ name: 'name', type: 'string' as const, desc: '联系人姓名', required: true }],
      successText: (args: unknown[]) => `已打开与「${String(args[0])}」的会话`,
      run: async (args: unknown[]) => {
        const c = await resolveContact(String(args[0]));
        const session = await sessionsRepo.ensureForContact(c.id);
        pushOverlay({ kind: 'chat', refId: session.id });
        eventBus.emit('session:opened', { sessionId: session.id });
        return { value: { sessionId: session.id } };
      },
    },
    {
      name: 'remove',
      desc: '删除与某联系人的整个会话（含全部消息）。',
      params: [{ name: 'name', type: 'string' as const, desc: '联系人姓名', required: true }],
      successText: (args: unknown[]) => `已删除与「${String(args[0])}」的会话`,
      requireConfirm: (args: unknown[]) =>
        `确定要删除与「${String(args[0])}」的整个会话吗？其中的所有消息都会被删除，无法恢复。`,
      run: async (args: unknown[]) => {
        const c = await resolveContact(String(args[0]));
        const list = await sessionsRepo.listByContact(c.id);
        for (const s of list) await sessionsRepo.remove(s.id);
        return { value: { removed: list.length } };
      },
    },
  ],
} satisfies ObjectDef;

/** 供其它 API 复用：按名字拿到一个确定存在的会话 */
export async function resolveSession(contactNameOrId: string): Promise<{
  session: { id: string };
  contact: Contact;
}> {
  const c = await resolveContact(contactNameOrId);
  const session = await sessionsRepo.ensureForContact(c.id);
  return { session, contact: c };
}

/** 供其它 API 复用：确认一个会话存在 */
export async function requireSession(id: string): Promise<{
  id: string;
  contactId: string;
}> {
  const s = await sessionsRepo.get(id);
  if (!s) throw new RuntimeError(`没有 id 为「${id}」的会话`, 'NameError');
  return s;
}
